import { useMemo } from 'react'

import { getResourceDefinition } from '~/client/src/shared/resources/definitions'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { classifyResourcePropertyInput } from '~/client/src/shared/resources/resourcePropertyInput'

import { PropertyData } from './formProperties'

export const useNewFormDefaults = ({
  resourceTypeId,
  resourceSchema,
}: {
  resourceTypeId: string
  resourceSchema: ResourceSchema
}) => {
  // Without a declared set every property comes pre-selected, which is how new
  // forms behaved before resources started declaring their defaults.
  const formDefaults = getResourceDefinition(resourceTypeId)?.form
  const defaultSelectedKeys = formDefaults?.defaultSelectedKeys
  const defaultRequiredKeys = formDefaults?.defaultRequiredKeys
  const mandatoryPropertyKeys = formDefaults?.mandatoryPropertyKeys

  const properties: PropertyData[] = useMemo(() => {
    // Pre-selected properties lead, in the order the resource declares them;
    // everything else keeps the project's own property order behind them.
    const declaredRank = (name?: string | null) => {
      const rank = defaultSelectedKeys?.indexOf(name || '') ?? -1
      return rank === -1 ? Infinity : rank
    }

    return [...resourceSchema.properties]
      .filter(
        prop => classifyResourcePropertyInput(prop).kind === 'editable-field',
      )
      .sort((a, b) => {
        const aRank = declaredRank(a.name)
        const bRank = declaredRank(b.name)
        if (aRank !== bRank) return aRank - bRank
        return (a.position ?? 0) - (b.position ?? 0)
      })
      .map((prop, index) => {
        const isLocked = !!mandatoryPropertyKeys?.includes(prop.name || '')
        return {
          propertyId: prop.id,
          label: prop.displayName || '',
          name: prop.name || '',
          type: prop.propertyType,
          configuration: prop.configuration,
          required:
            isLocked || !!defaultRequiredKeys?.includes(prop.name || ''),
          isHidden: isLocked
            ? false
            : defaultSelectedKeys
              ? !defaultSelectedKeys.includes(prop.name || '')
              : false,
          isLocked,
          orderIndex: index,
        }
      })
  }, [
    defaultRequiredKeys,
    defaultSelectedKeys,
    mandatoryPropertyKeys,
    resourceSchema.properties,
  ])

  return {
    properties,
    loading: resourceSchema.loading,
  }
}
